/**
 * Service Packages — top-level page (previously buried as an admin-only
 * Settings tab nobody could find; see release notes 43/44 "What's still
 * open": no web-side live delivery list/create view existed at all, only
 * the mobile agent app and WhatsApp could start/mark-delivered/verify a
 * delivery). This is that missing web surface, using "package" as the
 * user-facing term — OneImpact_Community_Service_Protocol_Questionnaire_EN
 * describes these as named community services/packages (counselling,
 * screening, transport/nutrition assistance, etc.), not "deliveries" — the
 * backend model, DB tables, mobile app and SDK all still say "delivery"
 * internally; only this page's copy was renamed, deliberately scoped to
 * avoid a much larger, riskier rename across every consumer.
 *   - "Package Types" tab (first — this is the primary entry point): a
 *     "Send a package" card per configured type, each with its own Send
 *     button, above the pre-existing type/catalog configuration
 *     (ServiceDeliverySettings, read-only for non-admins so anyone can at
 *     least see what's configured, matching how the backend already allows
 *     GET for any active user but restricts writes to admins).
 *   - "Status" tab (second): status counts, filters, and the live tracking
 *     table (start / mark delivered / notify / verify) — also has its own
 *     "Send package" entry point for convenience.
 * The send dialog itself supports picking multiple recipients at once
 * (checkboxes + search) so one package type can go out to several
 * beneficiaries in a single action.
 *
 * No requiredPermissions on the sidebar entry (see DashboardLayout.tsx) —
 * matches Issued Tickets' own pattern of being feature-gated (Digital
 * Companion ticketing must be enabled, since every package hangs off a
 * ComplaintTicket) rather than RBAC-gated.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Loader2,
  Locate,
  MessageCircle,
  Package,
  Search,
  Send,
  Settings,
  Truck,
  Users,
} from 'lucide-react';
import { useAppSelector } from '../store/hooks';
import { deliveriesAPI, complaintTicketsAPI, contactsAPI, messagesAPI } from '../services/api';
import ServiceDeliverySettings from '../components/ticketing/ServiceDeliverySettings';
import { cn } from '../lib/utils';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../components/ui/use-toast';

// ── Types (loose — mirror the dicts app/api/deliveries.py serializes) ──────

interface FormField {
  field_id: string;
  label: string;
  type?: string;
}

interface DeliveryType {
  id: number;
  label: string;
  archetype: string;
  requires_photo: boolean;
  requires_geolocation: boolean;
  requires_verification: boolean;
  requires_form?: FormField[] | null;
  escalation_hours: number | null;
  active: boolean;
}

interface DeliveryItem {
  id: number;
  name: string;
  unit: string | null;
  unit_value: number | null;
  currency: string | null;
}

interface Delivery {
  id: number;
  ticket_id: number;
  ticket_number: string | null;
  conversation_id: number | null;
  contact_name: string | null;
  delivery_type: DeliveryType | null;
  item: DeliveryItem | null;
  quantity: number;
  fulfillment_status: 'assigned' | 'in_progress' | 'delivered' | 'verified' | 'closed' | string;
  delivered_at: string | null;
  delivery_lat: number | null;
  delivery_lng: number | null;
  rejection_reason: string | null;
  total_value: number | null;
  currency_snapshot: string | null;
  created_at: string | null;
  // Docs/SERVICE_PACKAGE_AI_COUNSELLING_PIPELINE_PLAN.md
  ai_session_available: boolean;
  ai_session: { started_at: string; last_message_at: string | null; status: string } | null;
}

interface ContactOption {
  id: number;
  name?: string | null;
  phone_number?: string | null;
}

const STATUS_META: Record<string, { label: string; className: string }> = {
  assigned: { label: 'Assigned', className: 'bg-muted text-muted-foreground' },
  in_progress: { label: 'In progress', className: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
  delivered: { label: 'Delivered · pending review', className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
  verified: { label: 'Verified', className: 'bg-green-500/10 text-green-600 dark:text-green-400' },
  closed: { label: 'Closed', className: 'bg-green-500/10 text-green-600 dark:text-green-400' },
};

// Same glyph set as ServiceDeliverySettings.tsx's ARCHETYPES, kept in sync
// there via a code comment — used here only for the read-only catalog
// cards, not for editing.
const ARCHETYPE_GLYPH: Record<string, string> = {
  physical_item: '🧺',
  session_visit: '🗣️',
  voucher: '🎫',
  screening_sample: '🩺',
  referral_case: '⚖️',
};

const STATUS_ORDER: Array<{ key: string; label: string }> = [
  { key: 'assigned', label: 'Assigned' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'delivered', label: 'Pending review' },
  { key: 'verified', label: 'Verified' },
  { key: 'closed', label: 'Closed' },
];

function StatusBadge({ status }: { status: string }) {
  const meta = STATUS_META[status] || { label: status, className: 'bg-muted text-muted-foreground' };
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${meta.className}`}>
      {meta.label}
    </span>
  );
}

function errMsg(err: any, fallback: string) {
  return err?.response?.data?.detail || err?.message || fallback;
}

// Lightweight history for the detail pane's timeline — the backend only
// persists created_at/delivered_at as real timestamps, so "Started" and
// "Verified/Closed" are shown as reached/not-reached milestones without a
// fabricated date rather than inventing timestamps the API doesn't have.
function buildTimeline(d: Delivery): Array<{ label: string; detail: string | null; done: boolean }> {
  const started = d.fulfillment_status !== 'assigned';
  const delivered = ['delivered', 'verified', 'closed'].includes(d.fulfillment_status);
  const closed = ['verified', 'closed'].includes(d.fulfillment_status);
  return [
    { label: 'Assigned', detail: d.created_at ? new Date(d.created_at).toLocaleString() : null, done: true },
    { label: 'Started', detail: null, done: started },
    { label: 'Marked delivered', detail: d.delivered_at ? new Date(d.delivered_at).toLocaleString() : null, done: delivered },
    { label: d.delivery_type?.requires_verification === false ? 'Closed' : 'Verified', detail: null, done: closed },
  ];
}

export default function ServiceDeliveryPage() {
  const { user } = useAppSelector((state) => state.auth);
  const isAdmin = ['admin', 'administrator', 'super_admin'].includes((user?.role || '').toLowerCase());

  const [checkingEnabled, setCheckingEnabled] = useState(true);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Deliveries always hang off a ComplaintTicket, so this page needs the
    // same feature flag Issued Tickets checks (TicketsPage.tsx) — an
    // account with Digital Companion ticketing off has nowhere to attach a
    // delivery in the first place.
    complaintTicketsAPI
      .isEnabled()
      .then((res) => { if (!cancelled) setEnabled(Boolean(res?.enabled)); })
      .catch(() => { if (!cancelled) setEnabled(false); })
      .finally(() => { if (!cancelled) setCheckingEnabled(false); });
    return () => { cancelled = true; };
  }, []);

  // 2026-08-25: 'deliveries' (labeled "Track" in the rail nav below) is now
  // the default landing view rather than 'types' ("Configure") — tracking
  // is the daily task; configuring the catalog is occasional.
  const [mainTab, setMainTab] = useState<'types' | 'deliveries'>('deliveries');
  const [filter, setFilter] = useState<'mine' | 'all' | 'pending'>(isAdmin ? 'all' : 'mine');

  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // ── Track view: master/detail selection ─────────────────────────────────
  // Replaces the old flat table — a search box narrows the list client-side
  // (the `filter` chips above already narrow it server-side), and clicking a
  // row opens its full detail in the pane alongside instead of a row of
  // inline buttons.
  const [trackSearch, setTrackSearch] = useState('');
  const [selectedDeliveryId, setSelectedDeliveryId] = useState<number | null>(null);

  const loadDeliveries = useCallback(() => {
    setLoading(true);
    setListError(null);
    const params: any = {};
    if (filter === 'mine') params.mine = true;
    if (filter === 'pending') params.pending_verification = true;
    deliveriesAPI
      .listDeliveries(params)
      .then((data) => setDeliveries(data?.deliveries || []))
      .catch((err) => setListError(errMsg(err, 'Failed to load deliveries')))
      .finally(() => setLoading(false));
  }, [filter]);

  // ── Status counts ──────────────────────────────────────────────────────
  // "How many delivered" etc., independent of the table's own filter chip —
  // one lightweight call per status using limit=1 and reading the response's
  // `total` (the backend counts the full match, not just the returned page),
  // so this never needs a dedicated counts endpoint.
  const [statusCounts, setStatusCounts] = useState<Record<string, number> | null>(null);

  const loadStatusCounts = useCallback(() => {
    const scope = isAdmin ? {} : { mine: true };
    Promise.all(
      STATUS_ORDER.map(({ key }) =>
        deliveriesAPI
          .listDeliveries({ ...scope, fulfillment_status: key, limit: 1 })
          .then((data) => [key, data?.total ?? 0] as const)
          .catch(() => [key, 0] as const),
      ),
    ).then((pairs) => setStatusCounts(Object.fromEntries(pairs)));
  }, [isAdmin]);

  const refresh = useCallback(() => {
    loadDeliveries();
    loadStatusCounts();
  }, [loadDeliveries, loadStatusCounts]);

  useEffect(() => {
    if (enabled) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filter]);

  // ── Package catalog (available types) ───────────────────────────────────
  // Loaded once up front, not only when the "Send package" dialog opens —
  // this is also what renders the "available packages" cards above the list.

  const [types, setTypes] = useState<DeliveryType[]>([]);

  useEffect(() => {
    if (!enabled) return;
    deliveriesAPI.listDeliveryTypes(true).then((data) => setTypes(data || [])).catch(() => setTypes([]));
  }, [enabled]);

  // ── Create ("Send Package") ─────────────────────────────────────────────

  const [createOpen, setCreateOpen] = useState(false);
  // 2026-08-26: was ticket-backed (complaintTicketsAPI) — flagged live: an
  // agent could only pick a contact who already had a ComplaintTicket, so
  // anyone who'd never generated one was simply invisible in this picker.
  // Backed by the account's whole contact list instead; deliveriesAPI
  // now accepts contact_id directly and finds/creates a ticket for it
  // server-side (app/api/deliveries.py's _get_or_create_ticket_for_contact).
  const [contacts, setContacts] = useState<ContactOption[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [selectedContactIds, setSelectedContactIds] = useState<Set<number>>(new Set());

  const [selectedTypeId, setSelectedTypeId] = useState<string>('');
  const [typeItems, setTypeItems] = useState<DeliveryItem[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [quantity, setQuantity] = useState('1');
  const [creating, setCreating] = useState(false);

  const loadContacts = useCallback((search: string) => {
    setContactsLoading(true);
    contactsAPI
      .getContacts({ search: search.trim() || undefined, per_page: 50 })
      .then((data: any) => setContacts(data?.contacts || []))
      .catch(() => setContacts([]))
      .finally(() => setContactsLoading(false));
  }, []);

  const openCreate = (presetTypeId?: number) => {
    setCreateOpen(true);
    setSelectedContactIds(new Set());
    setContactSearch('');
    setTypeItems([]);
    setSelectedItemId('');
    setQuantity('1');

    if (presetTypeId) {
      handleSelectType(String(presetTypeId));
    } else {
      setSelectedTypeId('');
    }

    loadContacts('');
  };

  // Search is server-side (the account's full contact list can be large —
  // unlike the old ticket picker's one-time 100-row fetch, this can't just
  // filter a fixed client-side page), debounced so typing doesn't fire a
  // request per keystroke.
  useEffect(() => {
    if (!createOpen) return;
    const handle = setTimeout(() => loadContacts(contactSearch), 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactSearch, createOpen]);

  const toggleContactSelection = (id: number) => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllShown = () => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      contacts.forEach((c) => next.add(c.id));
      return next;
    });
  };

  const clearSelection = () => setSelectedContactIds(new Set());

  const handleSelectType = (value: string) => {
    setSelectedTypeId(value);
    setSelectedItemId('');
    setTypeItems([]);
    if (value) {
      deliveriesAPI.listItems(Number(value), true).then((data) => setTypeItems(data || [])).catch(() => setTypeItems([]));
    }
  };

  const handleCreate = async () => {
    if (selectedContactIds.size === 0) {
      toast({ title: 'Pick at least one contact', description: 'Choose who this package is going to.', variant: 'destructive' as any });
      return;
    }
    if (!selectedTypeId) {
      toast({ title: 'Pick a package type', description: 'Choose what is being provided.', variant: 'destructive' as any });
      return;
    }
    setCreating(true);
    const targets = Array.from(selectedContactIds);
    let succeeded = 0;
    const failures: string[] = [];
    for (const contactId of targets) {
      const contact = contacts.find((c) => c.id === contactId);
      const label = contact?.name || contact?.phone_number || `#${contactId}`;
      try {
        await deliveriesAPI.createDelivery({
          contact_id: contactId,
          delivery_type_id: Number(selectedTypeId),
          item_catalog_id: selectedItemId ? Number(selectedItemId) : undefined,
          quantity: Number(quantity) || 1,
        });
        succeeded += 1;
      } catch (err: any) {
        failures.push(`${label} (${errMsg(err, 'failed')})`);
      }
    }
    setCreating(false);
    if (succeeded > 0) {
      toast({
        title: succeeded === 1 ? 'Package sent' : `Package sent to ${succeeded} contacts`,
        description: failures.length > 0 ? `Failed for: ${failures.join(', ')}` : undefined,
        variant: failures.length > 0 ? ('destructive' as any) : undefined,
      });
      setCreateOpen(false);
      refresh();
    } else {
      toast({ title: "Couldn't send package", description: `Failed for: ${failures.join(', ')}`, variant: 'destructive' as any });
    }
  };

  // ── Start ──────────────────────────────────────────────────────────────

  const [startingId, setStartingId] = useState<number | null>(null);

  const handleStart = async (delivery: Delivery) => {
    setStartingId(delivery.id);
    try {
      const updated = await deliveriesAPI.startDelivery(delivery.id);
      setDeliveries((prev) => prev.map((d) => (d.id === delivery.id ? updated : d)));
      loadStatusCounts();
    } catch (err: any) {
      toast({ title: "Couldn't start delivery", description: errMsg(err, ''), variant: 'destructive' as any });
    } finally {
      setStartingId(null);
    }
  };

  // ── Start AI session ─────────────────────────────────────────────────
  // Docs/SERVICE_PACKAGE_AI_COUNSELLING_PIPELINE_PLAN.md — separate,
  // additive action alongside Notify/Start: primes the conversation so the
  // beneficiary's next WhatsApp reply drops into the linked category's
  // real AI counselling flow, and auto-completes this delivery when that
  // conversation closes as "satisfied." Only ever shown when the backend
  // reports a linked category exists (ai_session_available) — no client-
  // side guessing about config it can't see.

  const [aiSessionConfirmFor, setAiSessionConfirmFor] = useState<Delivery | null>(null);
  const [startingAiSessionId, setStartingAiSessionId] = useState<number | null>(null);

  const handleStartAiSession = async () => {
    if (!aiSessionConfirmFor) return;
    setStartingAiSessionId(aiSessionConfirmFor.id);
    try {
      const updated = await deliveriesAPI.startAiSession(aiSessionConfirmFor.id);
      setDeliveries((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
      loadStatusCounts();
      toast({ title: 'AI session started', description: `Waiting on a reply from ${aiSessionConfirmFor.contact_name || 'the beneficiary'}.` });
      setAiSessionConfirmFor(null);
    } catch (err: any) {
      toast({ title: "Couldn't start AI session", description: errMsg(err, ''), variant: 'destructive' as any });
    } finally {
      setStartingAiSessionId(null);
    }
  };

  function aiSessionLabel(session: Delivery['ai_session']): string | null {
    if (!session) return null;
    const hours = Math.max(0, Math.round((Date.now() - new Date(session.started_at).getTime()) / 3_600_000));
    switch (session.status) {
      case 'waiting': return `AI session waiting for reply — ${hours}h`;
      case 'in_progress': return `AI session in progress — ${hours}h`;
      case 'completed': return 'AI session completed';
      case 'expired': return `AI session expired — no reply after ${hours}h`;
      default: return null;
    }
  }

  // ── Mark delivered ────────────────────────────────────────────────────

  const [markingDelivery, setMarkingDelivery] = useState<Delivery | null>(null);
  const [attachmentId, setAttachmentId] = useState<number | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoName, setPhotoName] = useState<string | null>(null);
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState('');
  const [submittingDelivery, setSubmittingDelivery] = useState(false);

  const openMarkDelivered = (delivery: Delivery) => {
    setMarkingDelivery(delivery);
    setAttachmentId(null);
    setPhotoName(null);
    setLat('');
    setLng('');
    setFormValues({});
    setNotes('');
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !markingDelivery) return;
    setUploadingPhoto(true);
    setPhotoName(file.name);
    try {
      const uploaded = await deliveriesAPI.uploadDeliveryAttachment(markingDelivery.id, file);
      setAttachmentId(uploaded.attachment_id);
    } catch (err: any) {
      setPhotoName(null);
      toast({ title: 'Upload failed', description: errMsg(err, ''), variant: 'destructive' as any });
    } finally {
      setUploadingPhoto(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast({ title: 'Location unavailable', description: "This browser doesn't support geolocation.", variant: 'destructive' as any });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(String(pos.coords.latitude));
        setLng(String(pos.coords.longitude));
      },
      (err) => toast({ title: "Couldn't get location", description: err.message, variant: 'destructive' as any }),
    );
  };

  const handleMarkDelivered = async () => {
    if (!markingDelivery?.delivery_type) return;
    const deliveryType = markingDelivery.delivery_type;

    if (deliveryType.requires_photo && !attachmentId) {
      toast({ title: 'Photo required', description: `${deliveryType.label} needs a photo before it can be marked delivered.`, variant: 'destructive' as any });
      return;
    }
    if (deliveryType.requires_geolocation && (!lat || !lng)) {
      toast({ title: 'Location required', description: `${deliveryType.label} needs a location before it can be marked delivered.`, variant: 'destructive' as any });
      return;
    }
    const requiredFields = deliveryType.requires_form || [];
    const missing = requiredFields.filter((f) => !formValues[f.field_id]?.trim());
    if (missing.length > 0) {
      toast({ title: 'Missing fields', description: `Please fill in: ${missing.map((f) => f.label).join(', ')}`, variant: 'destructive' as any });
      return;
    }

    setSubmittingDelivery(true);
    try {
      const updated = await deliveriesAPI.markDelivered(markingDelivery.id, {
        attachment_ids: attachmentId ? [attachmentId] : undefined,
        form_responses: requiredFields.length > 0 ? formValues : undefined,
        lat: lat ? Number(lat) : undefined,
        lng: lng ? Number(lng) : undefined,
        notes: notes.trim() || undefined,
      });
      setDeliveries((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
      loadStatusCounts();
      toast({
        title: updated.fulfillment_status === 'closed' ? 'Delivered' : 'Marked delivered',
        description: updated.fulfillment_status === 'closed'
          ? "This delivery type doesn't require verification — it's closed."
          : 'Sent for supervisor verification.',
      });
      setMarkingDelivery(null);
    } catch (err: any) {
      toast({ title: "Couldn't mark delivered", description: errMsg(err, ''), variant: 'destructive' as any });
    } finally {
      setSubmittingDelivery(false);
    }
  };

  // ── Notify contact ────────────────────────────────────────────────────
  // Sends into the ticket's existing WhatsApp/app conversation via the same
  // endpoint the dashboard's own conversation reply box uses
  // (messagesAPI.sendMessage → POST /conversations/{id}/messages →
  // app/api/messages.py, which dispatches through the real channel adapter).
  // This only works while that conversation's WhatsApp session window is
  // open (the beneficiary has messaged in within the last 24h, which is
  // true for essentially every ticket here since it originated from an
  // inbound conversation) — a genuinely cold outreach after that window
  // needs a Meta-approved WhatsApp template, which is the same operational
  // prerequisite already flagged as blocking Proactive Outreach (OUT-2) in
  // release notes 43/45. Not built here; the send below will simply fail
  // with the channel adapter's own error in that case, surfaced as a toast.

  function composeNotifyMessage(d: Delivery): string {
    const name = d.contact_name || 'there';
    const typeLabel = d.delivery_type?.label || 'a service package';
    const itemPart = d.item?.name ? ` (${d.item.name})` : '';
    if (d.delivery_type?.archetype === 'session_visit') {
      return `Hi ${name}, you are being provided with ${typeLabel}${itemPart}. Is this a good time for your ${typeLabel.toLowerCase()} session?`;
    }
    return `Hi ${name}, you are being provided with ${typeLabel}${itemPart}. A community worker will be in touch with you about it soon.`;
  }

  const [notifyingDelivery, setNotifyingDelivery] = useState<Delivery | null>(null);
  const [notifyText, setNotifyText] = useState('');
  const [sendingNotify, setSendingNotify] = useState(false);

  const openNotify = (delivery: Delivery) => {
    setNotifyingDelivery(delivery);
    setNotifyText(composeNotifyMessage(delivery));
  };

  const handleSendNotify = async () => {
    if (!notifyingDelivery) return;
    if (!notifyingDelivery.conversation_id) {
      toast({ title: "Can't notify", description: 'This ticket has no linked conversation to send through.', variant: 'destructive' as any });
      return;
    }
    if (!notifyText.trim()) return;
    setSendingNotify(true);
    try {
      await messagesAPI.sendMessage(notifyingDelivery.conversation_id, {
        content: notifyText.trim(),
        message_type: 'outgoing',
        content_type: 'text',
        content_attributes: { triggered_by: 'service_package_notification', delivery_id: notifyingDelivery.id },
      });
      toast({ title: 'Message sent', description: `Sent to ${notifyingDelivery.contact_name || 'the beneficiary'} via their conversation.` });
      setNotifyingDelivery(null);
    } catch (err: any) {
      toast({
        title: "Couldn't send message",
        description: errMsg(err, "The beneficiary's WhatsApp session may be closed — they need to message in first, or this needs an approved WhatsApp template."),
        variant: 'destructive' as any,
      });
    } finally {
      setSendingNotify(false);
    }
  };

  // ── Verify / reject ──────────────────────────────────────────────────

  const [reviewingDelivery, setReviewingDelivery] = useState<Delivery | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const openReview = (delivery: Delivery) => {
    setReviewingDelivery(delivery);
    setReviewNotes('');
  };

  const handleApprove = async () => {
    if (!reviewingDelivery) return;
    setReviewing(true);
    try {
      const updated = await deliveriesAPI.verifyDelivery(reviewingDelivery.id, reviewNotes.trim() || undefined);
      setDeliveries((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
      loadStatusCounts();
      toast({ title: 'Package verified' });
      setReviewingDelivery(null);
    } catch (err: any) {
      toast({ title: "Couldn't verify", description: errMsg(err, ''), variant: 'destructive' as any });
    } finally {
      setReviewing(false);
    }
  };

  const handleReject = async () => {
    if (!reviewingDelivery) return;
    if (!reviewNotes.trim()) {
      toast({ title: 'Reason required', description: 'Tell the worker what needs to be fixed before rejecting.', variant: 'destructive' as any });
      return;
    }
    setReviewing(true);
    try {
      const updated = await deliveriesAPI.rejectDelivery(reviewingDelivery.id, reviewNotes.trim());
      setDeliveries((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
      loadStatusCounts();
      toast({ title: 'Sent back to the worker' });
      setReviewingDelivery(null);
    } catch (err: any) {
      toast({ title: "Couldn't reject", description: errMsg(err, ''), variant: 'destructive' as any });
    } finally {
      setReviewing(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────

  const filteredTrackDeliveries = trackSearch.trim()
    ? deliveries.filter((d) => {
        const q = trackSearch.trim().toLowerCase();
        return (
          (d.ticket_number || '').toLowerCase().includes(q) ||
          (d.contact_name || '').toLowerCase().includes(q) ||
          (d.delivery_type?.label || '').toLowerCase().includes(q)
        );
      })
    : deliveries;
  const selectedDelivery =
    filteredTrackDeliveries.find((d) => d.id === selectedDeliveryId) || filteredTrackDeliveries[0] || null;

  if (checkingEnabled) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center text-muted-foreground">
        <AlertTriangle className="h-6 w-6" />
        <p className="text-sm">Digital Companion ticketing isn't enabled for this account — service packages are tracked against tickets.</p>
      </div>
    );
  }

  // Horizontal underline sub-tabs instead of a side rail — sized to their
  // own label rather than stretched to fill a column, and the active one
  // is unambiguous via the underline + primary color rather than a subtle
  // bordered-card fill.
  const subTabClass = (active: boolean) =>
    cn(
      'inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-1 pb-2.5 text-sm font-semibold transition-colors',
      active ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground',
    );

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4 p-4 pb-0 md:p-6 md:pb-0">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {mainTab === 'deliveries' ? 'Track packages' : 'Configure & send'}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {mainTab === 'deliveries'
              ? 'Every service package moving through delivery, from assignment to verified.'
              : 'Manage what this account can offer, and send a package straight from its card.'}
          </p>
        </div>
        {mainTab === 'deliveries' ? (
          <Button onClick={() => openCreate()}>
            <Send className="mr-2 h-4 w-4" />
            Send a package
          </Button>
        ) : (
          <Button variant="outline" onClick={() => setMainTab('deliveries')}>
            View tracking
          </Button>
        )}
      </div>

      {/* 2026-08-25: replaces the side rail with a horizontal sub-tab bar —
          "Track" and "Configure" are top-level destinations rather than a
          buried settings tab, per stakeholder mockup v17, but laid out
          compactly like a normal settings sub-nav instead of a full-height
          left column. */}
      <div className="mt-4 flex gap-5 border-b px-4 md:px-6">
        <button type="button" onClick={() => setMainTab('deliveries')} className={subTabClass(mainTab === 'deliveries')}>
          <Truck className="h-4 w-4" />
          Track
        </button>
        <button type="button" onClick={() => setMainTab('types')} className={subTabClass(mainTab === 'types')}>
          <Settings className="h-4 w-4" />
          Configure
        </button>
      </div>

      <div className="p-4 md:p-6">
        {mainTab === 'types' ? (
            <div className="space-y-6">
              {/* Send a package — what this account can currently send, straight
                  from its configured Package Types. Each card's "Send" button
                  opens the create dialog with that type pre-selected. */}
              <Card className="rounded-2xl border-border/60 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold">Send a package</CardTitle>
                  <CardDescription>Pick what to provide — you'll choose who it goes to next.</CardDescription>
                </CardHeader>
                <CardContent>
                  {types.length === 0 ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">
                      No package types configured yet — add one below to start sending.
                    </p>
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {types.map((t) => (
                        <div
                          key={t.id}
                          className="flex items-start justify-between gap-3 rounded-xl border border-border/60 bg-muted/20 p-4 transition-colors hover:bg-muted/40"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 font-medium leading-tight">
                              <span className="text-lg leading-none">{ARCHETYPE_GLYPH[t.archetype] || '📦'}</span>
                              <span className="truncate">{t.label}</span>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1">
                              {t.requires_photo && <Badge variant="secondary" className="text-[10px] font-normal">Photo</Badge>}
                              {t.requires_geolocation && <Badge variant="secondary" className="text-[10px] font-normal">Location</Badge>}
                              {t.requires_verification && <Badge variant="secondary" className="text-[10px] font-normal">Verified</Badge>}
                              {t.archetype === 'session_visit' && <Badge variant="secondary" className="text-[10px] font-normal">Counselling check-in</Badge>}
                            </div>
                          </div>
                          <Button size="sm" onClick={() => openCreate(t.id)} className="shrink-0">
                            <Send className="mr-1.5 h-3.5 w-3.5" />
                            Send
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Separator />

              {/* Configuration — the pre-existing type/catalog editor, read-only
                  for non-admins so anyone can see what's configured even if they
                  can't change it. */}
              <ServiceDeliverySettings readOnly={!isAdmin} />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Status counts — "how many delivered" etc. at a glance, scoped to
                  My packages for a non-admin, account-wide for an admin;
                  independent of whichever filter chip is selected below. */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                {STATUS_ORDER.map(({ key, label }) => (
                  <Card key={key} className="rounded-xl border-border/60 shadow-sm">
                    <CardContent className="p-3">
                      <div className="text-2xl font-semibold tabular-nums">
                        {statusCounts ? statusCounts[key] ?? 0 : <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
                      </div>
                      <div className="text-xs text-muted-foreground">{label}</div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Master/detail — replaces the old flat table. The list stays a
                  bounded-height scroll region (viewport-relative, no ancestor
                  height chain needed) while the detail pane grows with the
                  page's natural scroll. */}
              <div className="grid gap-4 md:grid-cols-[340px_1fr]">
                <Card className="flex max-h-[70vh] flex-col overflow-hidden rounded-2xl border-border/60 shadow-sm">
                  <div className="space-y-2.5 border-b p-3">
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Search ticket, name, type…"
                        value={trackSearch}
                        onChange={(e) => setTrackSearch(e.target.value)}
                        className="h-8 pl-8 text-sm"
                      />
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      <Button variant={filter === 'mine' ? 'default' : 'outline'} size="sm" className="h-7 px-2.5 text-xs" onClick={() => setFilter('mine')}>
                        My packages
                      </Button>
                      {isAdmin && (
                        <Button variant={filter === 'all' ? 'default' : 'outline'} size="sm" className="h-7 px-2.5 text-xs" onClick={() => setFilter('all')}>
                          All
                        </Button>
                      )}
                      {isAdmin && (
                        <Button variant={filter === 'pending' ? 'default' : 'outline'} size="sm" className="h-7 px-2.5 text-xs" onClick={() => setFilter('pending')}>
                          Pending{statusCounts ? ` (${statusCounts.delivered ?? 0})` : ''}
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {listError && <p className="p-4 text-sm text-destructive">{listError}</p>}
                    {loading ? (
                      <div className="flex items-center justify-center py-12 text-muted-foreground">
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Loading packages…
                      </div>
                    ) : filteredTrackDeliveries.length === 0 ? (
                      <p className="p-6 text-center text-sm text-muted-foreground">
                        {deliveries.length === 0
                          ? 'No service packages here yet — click "Send a package" to assign one.'
                          : `No packages match "${trackSearch}".`}
                      </p>
                    ) : (
                      filteredTrackDeliveries.map((d) => (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => setSelectedDeliveryId(d.id)}
                          className={cn(
                            'flex w-full items-start gap-2.5 border-b p-3 text-left last:border-b-0 hover:bg-muted/40',
                            selectedDelivery?.id === d.id && 'bg-primary/5',
                          )}
                        >
                          <span className="mt-0.5 text-base leading-none">{ARCHETYPE_GLYPH[d.delivery_type?.archetype || ''] || '📦'}</span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <p className="truncate text-sm font-medium">{d.delivery_type?.label || 'Package'}</p>
                              <span className="shrink-0 font-mono text-[10px] text-muted-foreground">{d.ticket_number || `#${d.ticket_id}`}</span>
                            </div>
                            <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                              <span className="truncate">{d.contact_name || 'Unknown'}</span>
                              <span>·</span>
                              <StatusBadge status={d.fulfillment_status} />
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </Card>

                <Card className="rounded-2xl border-border/60 shadow-sm">
                  <CardContent className="p-5 md:p-6">
                    {!selectedDelivery ? (
                      <div className="flex min-h-[280px] items-center justify-center text-sm text-muted-foreground">
                        Select a package to see its details.
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <div className="flex flex-wrap items-start justify-between gap-3 border-b pb-4">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl leading-none">{ARCHETYPE_GLYPH[selectedDelivery.delivery_type?.archetype || ''] || '📦'}</span>
                            <div>
                              <h2 className="text-lg font-semibold">{selectedDelivery.delivery_type?.label || 'Package'}</h2>
                              <p className="font-mono text-xs text-muted-foreground">
                                {selectedDelivery.ticket_number || `#${selectedDelivery.ticket_id}`} · {selectedDelivery.contact_name || 'Unknown'}
                              </p>
                            </div>
                          </div>
                          <StatusBadge status={selectedDelivery.fulfillment_status} />
                        </div>

                        {selectedDelivery.rejection_reason && selectedDelivery.fulfillment_status === 'in_progress' && (
                          <p className="text-sm text-destructive">Sent back: {selectedDelivery.rejection_reason}</p>
                        )}
                        {aiSessionLabel(selectedDelivery.ai_session) && (
                          <p className="text-sm text-muted-foreground">{aiSessionLabel(selectedDelivery.ai_session)}</p>
                        )}

                        <div>
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Details</p>
                          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border sm:grid-cols-4">
                            <div className="bg-card p-3">
                              <p className="text-[10px] uppercase text-muted-foreground">Item</p>
                              <p className="text-sm font-medium">{selectedDelivery.item?.name || '—'}</p>
                            </div>
                            <div className="bg-card p-3">
                              <p className="text-[10px] uppercase text-muted-foreground">Quantity</p>
                              <p className="text-sm font-medium">{selectedDelivery.quantity}</p>
                            </div>
                            <div className="bg-card p-3">
                              <p className="text-[10px] uppercase text-muted-foreground">Value</p>
                              <p className="text-sm font-medium">
                                {selectedDelivery.total_value != null
                                  ? `${selectedDelivery.total_value.toLocaleString()} ${selectedDelivery.currency_snapshot || ''}`
                                  : '—'}
                              </p>
                            </div>
                            <div className="bg-card p-3">
                              <p className="text-[10px] uppercase text-muted-foreground">Conversation</p>
                              <p className="text-sm font-medium">{selectedDelivery.conversation_id ? 'Linked' : 'None'}</p>
                            </div>
                          </div>
                        </div>

                        <div>
                          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">History</p>
                          <div className="space-y-3 border-l pl-4">
                            {buildTimeline(selectedDelivery).map((step) => (
                              <div key={step.label} className="relative">
                                <span
                                  className={cn(
                                    'absolute -left-[21px] top-1 h-2 w-2 rounded-full',
                                    step.done ? 'bg-primary' : 'border border-border bg-background',
                                  )}
                                />
                                <p className={cn('text-sm font-medium', !step.done && 'text-muted-foreground')}>{step.label}</p>
                                {step.detail && <p className="text-xs text-muted-foreground">{step.detail}</p>}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 border-t pt-4">
                          {(selectedDelivery.fulfillment_status === 'assigned' || selectedDelivery.fulfillment_status === 'in_progress') && (
                            <Button size="sm" variant="outline" onClick={() => openNotify(selectedDelivery)} title="Message the beneficiary about this package">
                              <MessageCircle className="mr-1.5 h-4 w-4" />
                              Notify beneficiary
                            </Button>
                          )}
                          {selectedDelivery.fulfillment_status === 'assigned' && (
                            <Button size="sm" variant="outline" onClick={() => handleStart(selectedDelivery)} disabled={startingId === selectedDelivery.id}>
                              {startingId === selectedDelivery.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start'}
                            </Button>
                          )}
                          {selectedDelivery.fulfillment_status === 'assigned' && selectedDelivery.ai_session_available && !selectedDelivery.ai_session && (
                            <Button size="sm" onClick={() => setAiSessionConfirmFor(selectedDelivery)}>
                              Start AI session
                            </Button>
                          )}
                          {selectedDelivery.fulfillment_status === 'in_progress' && (
                            <Button size="sm" onClick={() => openMarkDelivered(selectedDelivery)}>Mark delivered</Button>
                          )}
                          {selectedDelivery.fulfillment_status === 'delivered' && isAdmin && (
                            <Button size="sm" variant="outline" onClick={() => openReview(selectedDelivery)}>Review</Button>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>

      {/* Send package */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send a service package</DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Package type</Label>
                <Select value={selectedTypeId} onValueChange={handleSelectType}>
                  <SelectTrigger><SelectValue placeholder="Choose a type" /></SelectTrigger>
                  <SelectContent>
                    {types.map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Quantity (per recipient)</Label>
                <Input type="number" min={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
              </div>
            </div>

            {typeItems.length > 0 && (
              <div className="space-y-2">
                <Label>Item (optional)</Label>
                <Select value={selectedItemId} onValueChange={setSelectedItemId}>
                  <SelectTrigger><SelectValue placeholder="Choose an item" /></SelectTrigger>
                  <SelectContent>
                    {typeItems.map((it) => (
                      <SelectItem key={it.id} value={String(it.id)}>{it.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5" />
                  Send to
                </Label>
                {selectedContactIds.size > 0 && (
                  <button type="button" onClick={clearSelection} className="text-xs text-muted-foreground underline-offset-2 hover:underline">
                    Clear ({selectedContactIds.size} selected)
                  </button>
                )}
              </div>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by contact name or phone number…"
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  className="pl-8"
                />
              </div>
              <div className="max-h-48 overflow-y-auto rounded-lg border border-border/60">
                {contactsLoading ? (
                  <div className="flex items-center justify-center py-6 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : contacts.length === 0 ? (
                  <p className="p-3 text-sm text-muted-foreground">No matching contacts.</p>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={selectAllShown}
                      className="w-full border-b border-border/40 px-3 py-1.5 text-left text-xs font-medium text-primary hover:bg-muted/40"
                    >
                      Select all {contacts.length} shown
                    </button>
                    {contacts.map((c) => {
                      const checked = selectedContactIds.has(c.id);
                      return (
                        <label
                          key={c.id}
                          className={`flex w-full cursor-pointer items-center gap-3 border-b border-border/40 px-3 py-2 text-sm last:border-b-0 hover:bg-muted/40 ${
                            checked ? 'bg-primary/5' : ''
                          }`}
                        >
                          <Checkbox checked={checked} onCheckedChange={() => toggleContactSelection(c.id)} />
                          <span className="flex-1 truncate">
                            <span className="font-medium">{c.name || 'Unknown contact'}</span>
                            {c.phone_number && (
                              <span className="ml-1.5 font-mono text-xs text-muted-foreground">{c.phone_number}</span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating || selectedContactIds.size === 0}>
              {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              {selectedContactIds.size > 1 ? `Send to ${selectedContactIds.size} contacts` : 'Send package'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark delivered */}
      <Dialog open={!!markingDelivery} onOpenChange={(open) => !open && setMarkingDelivery(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Mark delivered — {markingDelivery?.delivery_type?.label}</DialogTitle>
            <p className="font-mono text-xs text-muted-foreground">
              {markingDelivery?.ticket_number || `#${markingDelivery?.ticket_id}`} · {markingDelivery?.contact_name || 'Unknown'}
            </p>
          </DialogHeader>

          {markingDelivery?.delivery_type && (
            <div className="space-y-4 py-2">
              {markingDelivery.delivery_type.requires_photo && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1"><Package className="h-3.5 w-3.5" /> Proof photo</Label>
                  <Input type="file" accept="image/*,application/pdf" onChange={handlePhotoChange} disabled={uploadingPhoto} />
                  {uploadingPhoto && <p className="text-xs text-muted-foreground">Uploading…</p>}
                  {photoName && !uploadingPhoto && attachmentId && (
                    <p className="text-xs text-green-600 dark:text-green-400">Uploaded: {photoName}</p>
                  )}
                </div>
              )}

              {markingDelivery.delivery_type.requires_geolocation && (
                <div className="space-y-2">
                  <Label>Location</Label>
                  <div className="flex gap-2">
                    <Input placeholder="Latitude" value={lat} onChange={(e) => setLat(e.target.value)} />
                    <Input placeholder="Longitude" value={lng} onChange={(e) => setLng(e.target.value)} />
                    <Button type="button" variant="outline" size="icon" onClick={useMyLocation} title="Use my current location">
                      <Locate className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {(markingDelivery.delivery_type.requires_form || []).map((field) => (
                <div key={field.field_id} className="space-y-2">
                  <Label>{field.label}</Label>
                  <Input
                    value={formValues[field.field_id] || ''}
                    onChange={(e) => setFormValues((prev) => ({ ...prev, [field.field_id]: e.target.value }))}
                  />
                </div>
              ))}

              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea placeholder="Handed to guardian, household of 4…" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkingDelivery(null)} disabled={submittingDelivery}>Cancel</Button>
            <Button onClick={handleMarkDelivered} disabled={submittingDelivery || uploadingPhoto}>
              {submittingDelivery ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Mark delivered
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notify contact */}
      <Dialog open={!!notifyingDelivery} onOpenChange={(open) => !open && setNotifyingDelivery(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Message {notifyingDelivery?.contact_name || 'beneficiary'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Sent into their existing WhatsApp/app conversation for {notifyingDelivery?.ticket_number || `ticket #${notifyingDelivery?.ticket_id}`}.
              {notifyingDelivery?.delivery_type?.archetype === 'session_visit' && (
                <> This is a counselling-type package, so it's phrased as a check-in on timing rather than a plain notification.</>
              )}
            </p>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea value={notifyText} onChange={(e) => setNotifyText(e.target.value)} rows={4} />
            </div>
            {!notifyingDelivery?.conversation_id && (
              <p className="text-xs text-destructive">This ticket has no linked conversation — sending isn't possible.</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNotifyingDelivery(null)} disabled={sendingNotify}>Cancel</Button>
            <Button onClick={handleSendNotify} disabled={sendingNotify || !notifyText.trim() || !notifyingDelivery?.conversation_id}>
              {sendingNotify ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Send message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start AI session — confirm */}
      <Dialog open={!!aiSessionConfirmFor} onOpenChange={(open) => !open && setAiSessionConfirmFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Start an AI session?</DialogTitle>
            <p className="font-mono text-xs text-muted-foreground">
              {aiSessionConfirmFor?.ticket_number || `#${aiSessionConfirmFor?.ticket_id}`} · {aiSessionConfirmFor?.contact_name || 'Unknown'}
            </p>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <p>
              This will let the AI hold the {aiSessionConfirmFor?.delivery_type?.label?.toLowerCase() || 'counselling'} conversation
              with <strong>{aiSessionConfirmFor?.contact_name || 'the beneficiary'}</strong> directly over WhatsApp,
              instead of a human delivering it. A message goes out now asking if it's a good time.
            </p>
            <p className="text-muted-foreground">
              This package auto-completes when the AI closes the conversation as resolved — no need to click Mark delivered afterward.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiSessionConfirmFor(null)} disabled={startingAiSessionId != null}>Cancel</Button>
            <Button onClick={handleStartAiSession} disabled={startingAiSessionId != null}>
              {startingAiSessionId != null ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Start AI session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verify / reject */}
      <Dialog open={!!reviewingDelivery} onOpenChange={(open) => !open && setReviewingDelivery(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Review package — {reviewingDelivery?.delivery_type?.label}</DialogTitle>
            <p className="font-mono text-xs text-muted-foreground">
              {reviewingDelivery?.ticket_number || `#${reviewingDelivery?.ticket_id}`} · {reviewingDelivery?.contact_name || 'Unknown'}
            </p>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="rounded-md border border-border/60 p-3 text-sm">
              <div className="text-muted-foreground">Ticket</div>
              <div className="font-mono text-xs">{reviewingDelivery?.ticket_number || `#${reviewingDelivery?.ticket_id}`}</div>
              {reviewingDelivery?.delivered_at && (
                <>
                  <div className="mt-2 text-muted-foreground">Delivered at</div>
                  <div>{new Date(reviewingDelivery.delivered_at).toLocaleString()}</div>
                </>
              )}
              {reviewingDelivery?.delivery_lat != null && reviewingDelivery?.delivery_lng != null && (
                <>
                  <div className="mt-2 text-muted-foreground">Location</div>
                  <div className="font-mono">{reviewingDelivery.delivery_lat.toFixed(4)}°, {reviewingDelivery.delivery_lng.toFixed(4)}°</div>
                </>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notes (required if rejecting)</Label>
              <Textarea placeholder="Reason for rejection…" value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="text-destructive" onClick={handleReject} disabled={reviewing}>Reject</Button>
            <Button onClick={handleApprove} disabled={reviewing}>
              {reviewing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
